import React from 'react';
import { Box, Button, Divider, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { createExtra, createPromo, deleteExtra, deletePromo, Extra, listExtras, listPromos, Promo, updateExtra, updatePromo } from '../../api/hotels.ts';
import { toastError, toastSuccess } from '../../shared/ui/toast.ts';

export default function PromosExtrasTab({ hotelId }:{ hotelId: number }){
  const [promos, setPromos] = React.useState<Promo[]>([]);
  const [extras, setExtras] = React.useState<Extra[]>([]);
  const [p, setP] = React.useState<Promo>({ code:'', type:'PERCENT', value:0, active:true, validFrom:'', validTo:'', minNights:1 });
  const [e, setE] = React.useState<Extra>({ code:'', name:'', priceType:'PER_STAY', price:0, active:true });

  const load = async () => {
    try{
      const [pp, ee] = await Promise.all([listPromos(hotelId), listExtras(hotelId)]);
      setPromos(pp||[]);
      setExtras(ee||[]);
    }catch{ /* ignore */ }
  };
  React.useEffect(()=>{ load(); }, [hotelId]);

  const addPromo = async () => {
    try{ await createPromo(hotelId, p); toastSuccess('Promo created'); setP({ code:'', type:'PERCENT', value:0, active:true, validFrom:'', validTo:'', minNights:1 }); load(); }catch(err:any){ toastError(err?.message||'Failed'); }
  };
  const addExtra = async () => {
    try{ await createExtra(hotelId, e); toastSuccess('Extra created'); setE({ code:'', name:'', priceType:'PER_STAY', price:0, active:true }); load(); }catch(err:any){ toastError(err?.message||'Failed'); }
  };

  const onPromoField = (field:keyof Promo, val:any) => setP(s=>({ ...s, [field]: val } as Promo));
  const onExtraField = (field:keyof Extra, val:any) => setE(s=>({ ...s, [field]: val } as Extra));

  const togglePromoActive = async (row:Promo) => { await updatePromo(hotelId, row.id!, { ...row, active: !row.active }); load(); };
  const toggleExtraActive = async (row:Extra) => { await updateExtra(hotelId, row.id!, { ...row, active: !row.active }); load(); };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Promos</Typography>
        <Stack direction="row" spacing={2} mt={1}>
          <TextField label="Code" value={p.code} onChange={e=>onPromoField('code', e.target.value)} size="small" />
          <Select size="small" value={p.type} onChange={e=>onPromoField('type', e.target.value)}>
            <MenuItem value="PERCENT">PERCENT</MenuItem>
            <MenuItem value="FIXED">FIXED</MenuItem>
          </Select>
          <TextField label="Value" type="number" value={p.value} onChange={e=>onPromoField('value', Number(e.target.value))} size="small" />
          <TextField label="Min nights" type="number" value={p.minNights||''} onChange={e=>onPromoField('minNights', Number(e.target.value)||null)} size="small" />
          <FormControlLabel control={<Checkbox checked={!!p.active} onChange={e=>onPromoField('active', e.target.checked)} />} label="Active" />
          <Button variant="contained" onClick={addPromo}>Add</Button>
        </Stack>
        <Table size="small" sx={{ mt:2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell><TableCell>Type</TableCell><TableCell>Value</TableCell><TableCell>Min nights</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promos.map(row=> (
              <TableRow key={row.id}>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.value}</TableCell>
                <TableCell>{row.minNights||'-'}</TableCell>
                <TableCell><Checkbox checked={!!row.active} onChange={()=>togglePromoActive(row)} /></TableCell>
                <TableCell align="right"><IconButton onClick={async ()=>{ await deletePromo(hotelId, row.id!); load(); }}><DeleteIcon/></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Divider/>
      <Box>
        <Typography variant="h6">Extras</Typography>
        <Stack direction="row" spacing={2} mt={1}>
          <TextField label="Code" value={e.code} onChange={ev=>onExtraField('code', ev.target.value)} size="small" />
          <TextField label="Name" value={e.name} onChange={ev=>onExtraField('name', ev.target.value)} size="small" />
          <Select size="small" value={e.priceType} onChange={ev=>onExtraField('priceType', ev.target.value)}>
            <MenuItem value="PER_STAY">PER_STAY</MenuItem>
            <MenuItem value="PER_NIGHT">PER_NIGHT</MenuItem>
            <MenuItem value="PER_PERSON">PER_PERSON</MenuItem>
          </Select>
          <TextField label="Price" type="number" value={e.price} onChange={ev=>onExtraField('price', Number(ev.target.value))} size="small" />
          <FormControlLabel control={<Checkbox checked={!!e.active} onChange={ev=>onExtraField('active', ev.target.checked)} />} label="Active" />
          <Button variant="contained" onClick={addExtra}>Add</Button>
        </Stack>
        <Table size="small" sx={{ mt:2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>Price</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {extras.map(row=> (
              <TableRow key={row.id}>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.priceType}</TableCell>
                <TableCell>{row.price}</TableCell>
                <TableCell><Checkbox checked={!!row.active} onChange={()=>toggleExtraActive(row)} /></TableCell>
                <TableCell align="right"><IconButton onClick={async ()=>{ await deleteExtra(hotelId, row.id!); load(); }}><DeleteIcon/></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}


